/**
 * B2B page — isolated JS
 *
 * 1. CTA behaviour: every button on the page carries [data-b2b-cta] and opens
 *    the contact modal. If no modal is present (e.g. the section was removed in
 *    the theme editor) it falls back to scrolling to the contact section, so no
 *    CTA is ever a dead end.
 * 2. Modal open/close for [data-b2b-open-modal] / [data-b2b-modal].
 * 3. Native Gorgias contact form mounts in the modal; opening nudges layout
 *    via resize because the embed may initialize while display:none.
 */
(() => {
  const SELECTOR_CTA = '[data-b2b-cta]';
  const SELECTOR_OPEN = '[data-b2b-open-modal]';
  const SELECTOR_MODAL = '[data-b2b-modal]';
  const SELECTOR_CONTACT = '[data-b2b-contact]';
  const GORGIAS_ORIGIN = 'https://contact.gorgias.help';
  const GORGIAS_MESSAGE_SOURCE = 'ultimate-setup-b2b-gorgias';
  const GORGIAS_INLINE_MESSAGE_SOURCE = 'ultimate-setup-b2b-inline-gorgias';
  /* Must match the locale keys defined in the Gorgias B2B Extra HTML's COPY object. */
  const SUPPORTED_LOCALES = ['de', 'en'];

  let lastModalOpener = null;
  let gorgiasBridgeReady = false;
  let inlineLockedScrollX = null;
  let inlineLockedScrollY = null;

  const getModal = () => document.querySelector(SELECTOR_MODAL);
  const getContact = () => document.querySelector(SELECTOR_CONTACT);

  const isFocusable = (element) => {
    if (!element || typeof element.matches !== 'function') return false;
    return element.matches(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
  };

  const releaseModalFocus = (modal) => {
    const active = document.activeElement;
    if (modal && active && modal.contains(active) && typeof active.blur === 'function') {
      active.blur();
    }

    if (lastModalOpener && document.contains(lastModalOpener) && isFocusable(lastModalOpener)) {
      lastModalOpener.focus({ preventScroll: true });
      return;
    }

    if (document.body && typeof document.body.focus === 'function') {
      document.body.setAttribute('tabindex', '-1');
      document.body.focus({ preventScroll: true });
      document.body.removeAttribute('tabindex');
    }
  };

  const FORM_RESET_ATTR = 'b2bNeedsFormReset';
  const ORIGINAL_SRC_ATTR = 'b2bOriginalSrc';

  const getGorgiasIframe = (modal) => {
    if (!modal) return null;
    const slot = modal.querySelector('.b2b-modal__form-slot');
    if (!slot) return null;
    return (
      slot.querySelector('iframe[data-gorgias-iframe-contact-form]') || slot.querySelector('iframe')
    );
  };

  const normalizeGorgiasEmbedSrc = (src) => {
    if (!src) return '';

    try {
      const url = new URL(src, window.location.href);
      url.searchParams.delete('_b2b_reload');
      return url.toString();
    } catch (error) {
      return src;
    }
  };

  const preserveGorgiasIframeSrc = (iframe) => {
    if (!iframe) return '';

    const currentSrc = normalizeGorgiasEmbedSrc((iframe.getAttribute('src') || iframe.src || '').trim());
    if (currentSrc && currentSrc !== 'about:blank') {
      if (!iframe.dataset[ORIGINAL_SRC_ATTR]) {
        iframe.dataset[ORIGINAL_SRC_ATTR] = currentSrc;
      }
    }

    return iframe.dataset[ORIGINAL_SRC_ATTR] || '';
  };

  const reloadGorgiasIframe = (modal) => {
    const iframe = getGorgiasIframe(modal);
    if (!iframe) return false;

    const originalSrc = preserveGorgiasIframeSrc(iframe);
    if (!originalSrc) return false;

    try {
      const url = new URL(originalSrc, window.location.href);
      url.searchParams.set('_b2b_reload', String(Date.now()));
      const reloadSrc = url.toString();
      iframe.setAttribute('src', reloadSrc);
      iframe.src = reloadSrc;
    } catch (error) {
      iframe.setAttribute('src', originalSrc);
      iframe.src = originalSrc;
    }

    return true;
  };

  const markGorgiasFormForReset = (modal) => {
    if (!modal) return;
    preserveGorgiasIframeSrc(getGorgiasIframe(modal));
    modal.dataset[FORM_RESET_ATTR] = '1';
  };

  const maybeResetGorgiasFormOnOpen = (modal) => {
    if (!modal || modal.dataset[FORM_RESET_ATTR] !== '1') return false;

    if (!reloadGorgiasIframe(modal)) return false;

    delete modal.dataset[FORM_RESET_ATTR];
    modal.classList.remove('b2b-modal--success');
    return true;
  };

  const isMessageFromModalIframe = (event, modal) => {
    const iframe = getGorgiasIframe(modal);
    if (!iframe || !iframe.contentWindow) return true;

    if (event.source === iframe.contentWindow) return true;

    /* Nested Gorgias subframe inside the modal embed — origin is already strict. */
    return true;
  };

  const enterModalSuccessMode = () => {
    const modal = getModal();
    if (!modal || !modal.classList.contains('is-open')) return;
    modal.classList.add('b2b-modal--success');
    window.requestAnimationFrame(() => {
      window.dispatchEvent(new Event('resize'));
    });
  };

  // The hero ships a modal and the standalone modal section can add another.
  // Keep the first and drop the rest so only one ever opens.
  const dedupeModals = () => {
    const modals = document.querySelectorAll(SELECTOR_MODAL);
    for (let i = 1; i < modals.length; i += 1) modals[i].remove();
  };

  /* ---------------------------------------------------------------- modal */

  const openModal = (trigger) => {
    const modal = getModal();
    if (!modal) return false;

    if (trigger) lastModalOpener = trigger;

    maybeResetGorgiasFormOnOpen(modal);
    preserveGorgiasIframeSrc(getGorgiasIframe(modal));

    modal.classList.remove('b2b-modal--success');
    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('b2b-modal-open');
    modal.querySelector('[data-b2b-close-modal], .b2b-modal__close')?.focus?.();
    // Gorgias may mount while the dialog is display:none — nudge layout once visible
    window.requestAnimationFrame(() => {
      window.dispatchEvent(new Event('resize'));
    });
    return true;
  };

  const closeModal = ({ resetForm = false } = {}) => {
    const modal = getModal();
    if (!modal) return;

    releaseModalFocus(modal);
    modal.classList.remove('is-open', 'b2b-modal--success');
    modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('b2b-modal-open');

    if (resetForm) {
      markGorgiasFormForReset(modal);
    }
  };

  const restoreInlineLockedScroll = () => {
    if (inlineLockedScrollX === null || inlineLockedScrollY === null) return;
    const x = inlineLockedScrollX;
    const y = inlineLockedScrollY;
    window.scrollTo(x, y);
    window.requestAnimationFrame(() => {
      window.scrollTo(x, y);
    });
  };

  /*
   * The B2B Gorgias form renders inside a cross-origin iframe
   * (contact.gorgias.help), so its script cannot read window.location of
   * this storefront page — and document.referrer is unreliable there
   * (origin-only under the browser's default strict-origin-when-cross-origin
   * policy). Read the real page locale here and answer the iframe's request
   * with it over postMessage.
   */
  const detectPageLocale = () => {
    try {
      const segments = (window.location.pathname || '/').split('/').filter(Boolean);
      if (!segments.length) return 'de';
      const first = String(segments[0] || '').toLowerCase();
      if (SUPPORTED_LOCALES.includes(first)) return first;
      const short = first.split('-')[0];
      if (SUPPORTED_LOCALES.includes(short)) return short;
    } catch (e) {
      /* ignore */
    }
    return 'de';
  };

  const postLocale = (targetWindow, source = GORGIAS_MESSAGE_SOURCE, type = 'B2B_LOCALE') => {
    if (!targetWindow) return;
    try {
      targetWindow.postMessage({ source, type, locale: detectPageLocale() }, '*');
    } catch (e) {
      /* Ignore cross-origin delivery failures. */
    }
  };

  const onGorgiasMessage = (event) => {
    if (event.origin !== GORGIAS_ORIGIN) return;

    const data = event.data;
    if (!data || typeof data !== 'object') return;

    /* Answer directly to the requesting window — no modal/iframe lookup
       needed, and works even if the modal reference is momentarily stale. */
    if (data.source === GORGIAS_MESSAGE_SOURCE && data.type === 'B2B_LOCALE_REQUEST') {
      postLocale(event.source);
      return;
    }

    /* Inline form only — keep modal bridge untouched. */
    if (data.source === GORGIAS_INLINE_MESSAGE_SOURCE) {
      if (data.type === 'B2B_INLINE_LOCALE_REQUEST') {
        postLocale(event.source, GORGIAS_INLINE_MESSAGE_SOURCE, 'B2B_INLINE_LOCALE');
        return;
      }

      if (data.type === 'B2B_INLINE_LOCK_SCROLL') {
        inlineLockedScrollX = window.scrollX;
        inlineLockedScrollY = window.scrollY;
        return;
      }

      if (data.type === 'B2B_INLINE_PRESERVE_SCROLL') {
        restoreInlineLockedScroll();
        window.setTimeout(restoreInlineLockedScroll, 0);
        window.setTimeout(restoreInlineLockedScroll, 100);
        window.setTimeout(restoreInlineLockedScroll, 300);
      }
      return;
    }

    if (data.source !== GORGIAS_MESSAGE_SOURCE) return;

    const modal = getModal();
    if (!modal) return;

    if (!isMessageFromModalIframe(event, modal)) return;

    if (data.type === 'B2B_GORGIAS_SUCCESS') {
      enterModalSuccessMode();
      return;
    }

    if (data.type === 'B2B_GORGIAS_CLOSE') {
      closeModal({ resetForm: true });
    }
  };

  const initGorgiasBridge = () => {
    if (gorgiasBridgeReady) return;
    window.addEventListener('message', onGorgiasMessage);
    gorgiasBridgeReady = true;
  };

  /* -------------------------------------------------------------- scrolling */

  // Sticky headers vary per template, so measure whatever is actually fixed
  // at the top instead of hard-coding an offset.
  const headerOffset = () => {
    const header = document.querySelector(
      '.header-wrapper, .section-header, header.header, sticky-header'
    );
    if (!header) return 24;
    const { position } = window.getComputedStyle(header);
    if (position !== 'fixed' && position !== 'sticky') return 24;
    return header.getBoundingClientRect().height + 24;
  };

  const scrollToContact = () => {
    const contact = getContact();
    if (!contact) return false;

    const top = contact.getBoundingClientRect().top + window.pageYOffset - headerOffset();
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    window.scrollTo({ top: Math.max(top, 0), behavior: reduceMotion ? 'auto' : 'smooth' });
    return true;
  };

  /* ---------------------------------------------------------------- events */

  const onClick = (event) => {
    const cta = event.target.closest(SELECTOR_CTA);
    if (cta) {
      // Every CTA opens the contact modal. If no modal is on the page, fall
      // back to scrolling to the contact section, then to the plain anchor.
      if (openModal(cta) || scrollToContact()) event.preventDefault();
      return;
    }

    const openTrigger = event.target.closest(SELECTOR_OPEN);
    if (openTrigger) {
      event.preventDefault();
      openModal(openTrigger);
      return;
    }

    const modal = event.target.closest(SELECTOR_MODAL);
    if (!modal) return;

    if (event.target.closest('[data-b2b-close-modal]')) {
      event.preventDefault();
      closeModal();
    }
  };

  const onKeyDown = (event) => {
    if (event.key === 'Escape') closeModal();
  };

  /* --------------------------------------------------------------- sliders */

  // Scroll-snap carousel: the track does the scrolling and snapping natively,
  // so arrows/dots only need to nudge scrollLeft. Works with touch and
  // keyboard for free, and degrades to a plain scroller without JS.
  const initSlider = (root) => {
    // Guard against double-binding when the theme editor re-renders a section
    if (root.dataset.b2bSliderReady) return;

    const track = root.querySelector('[data-b2b-slider-track]');
    if (!track) return;

    // Liquid renders a legacy single image as a fallback so a row can never be
    // empty. Once real slides exist it is redundant, so drop it.
    const fallback = track.querySelector('[data-b2b-slide-fallback]');
    if (fallback && track.children.length > 1) fallback.remove();

    const slides = [...track.children];
    root.dataset.b2bSliderReady = '1';

    // Controls are hidden by CSS until .is-ready, so a single slide never shows
    // dead arrows regardless of what the markup emitted.
    if (slides.length < 2) return;

    const dotsHost = root.querySelector('[data-b2b-slider-dots]');
    if (dotsHost) {
      dotsHost.innerHTML = '';
      slides.forEach((_, i) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'b2b-slider__dot' + (i === 0 ? ' is-active' : '');
        dot.dataset.b2bSliderDot = String(i);
        dot.setAttribute('aria-label', 'Bild ' + (i + 1));
        dotsHost.appendChild(dot);
      });
    }

    root.classList.add('is-ready');

    const dots = [...root.querySelectorAll('[data-b2b-slider-dot]')];

    const currentIndex = () => {
      const width = track.clientWidth || 1;
      return Math.max(0, Math.min(slides.length - 1, Math.round(track.scrollLeft / width)));
    };

    const goTo = (index) => {
      // Wrap around so both arrows stay useful at the ends.
      const wrapped = (index + slides.length) % slides.length;
      track.scrollTo({ left: wrapped * track.clientWidth, behavior: 'smooth' });
    };

    const syncDots = () => {
      const active = currentIndex();
      dots.forEach((dot, i) => dot.classList.toggle('is-active', i === active));
    };

    root.querySelector('[data-b2b-slider-prev]')?.addEventListener('click', () => goTo(currentIndex() - 1));
    root.querySelector('[data-b2b-slider-next]')?.addEventListener('click', () => goTo(currentIndex() + 1));
    dots.forEach((dot) => {
      dot.addEventListener('click', () => goTo(Number(dot.dataset.b2bSliderDot) || 0));
    });

    let ticking = false;
    track.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(() => {
          syncDots();
          ticking = false;
        });
      },
      { passive: true }
    );

    syncDots();
  };

  const initAllSliders = () => document.querySelectorAll('[data-b2b-slider]').forEach(initSlider);

  document.addEventListener('click', onClick);
  document.addEventListener('keydown', onKeyDown);

  dedupeModals();
  initGorgiasBridge();
  initAllSliders();

  // Re-init after a theme-editor block/section re-render
  document.addEventListener('shopify:section:load', () => {
    dedupeModals();
    initAllSliders();
  });
  document.addEventListener('shopify:block:select', initAllSliders);

})();
